/**
 * Large UPS edit body for the descriptor engine (#997 Story 6.5, mirror of
 * `SmallUpsEngineEditFormBody`).
 *
 * Winston's ADR (parity by identity): the engine renders the EXISTING bespoke
 * `LargeUpsEditFormContent` — the user-approved Large UPS editor (field set,
 * order, labels, placeholders, `FormTwoOptionChoice` type toggle, the
 * yes-gated `powerModelOfExistingUps` field, the per-unit attachment carousel,
 * Additional-Info card) — bound to the engine's edit-session state. The engine
 * owns list/save/flush/attachment lifecycle (the 6.1/6.2 fixes), the section
 * owns its view.
 *
 * Divergence from `SmallUpsEngineEditFormBody`: `LargeUpsEditFormContent` gates
 * its Additional-Info card behind `showAdditionalInformation` (default false),
 * so the host computes it (via `shouldShowLargeUpsAdditionalInformationInUnitEditor`)
 * and threads it + `showFullDetails` through the `editBody` closure.
 *
 * Binding contract:
 * - `formData` is LARGE-UPS-shaped: the engine's editor field map is coerced
 *   back through `descriptorFieldMapToPatch`, plus identity (`name` ←
 *   `instanceName`, `status` ← `instanceStatus`) and the unit id (`id` ←
 *   `configurationId`, which `LargeUpsEditFormContent` uses to scope its per-unit
 *   attachment card — 6.1).
 * - `updateField` routes identity keys to the engine identity setters and every
 *   data field to `setInstanceField`, converting bespoke values to the engine's
 *   editor forms (undefined → '', boolean kept).
 */

import React, { useCallback, useMemo } from 'react';

import { LargeUpsEditFormContent } from '../../../../components/largeups/LargeUpsEditFormContent';
import { normalizeLargeUpsStatus } from '../../../../components/largeups/largeUpsListUtils';
import { descriptorFieldMapToPatch } from '../../../sections/descriptorEngineAdapter';
import { LARGE_UPS_DESCRIPTOR } from '../../../sections/largeUps.descriptor';

import type { RepeatableInstanceEditFormRenderProps } from '../../../../components/repeatableSections/repeatableInstanceEditFormTypes';
import type { LargeUpsConfiguration } from '../../../../types/largeUps';

export type LargeUpsEngineEditFormBodyProps =
  RepeatableInstanceEditFormRenderProps & {
    /** Large UPS gates its Additional-Info card (unlike Small UPS). */
    showAdditionalInformation?: boolean;
    showFullDetails?: boolean;
  };

export function LargeUpsEngineEditFormBody({
  instanceFields,
  setInstanceField,
  instanceName,
  instanceStatus,
  setInstanceName,
  setInstanceStatus,
  disabled,
  requestStage,
  requestId,
  configurationId,
  attachmentsBySection,
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles,
  onFilesSelected,
  offlineMode,
  onCaptionChange,
  pendingCaptionEdits,
  markDirty,
  showAdditionalInformation,
  showFullDetails,
}: LargeUpsEngineEditFormBodyProps): React.ReactElement {
  const toPatch = useMemo(
    () => descriptorFieldMapToPatch(LARGE_UPS_DESCRIPTOR),
    [],
  );

  const formData = useMemo((): Partial<LargeUpsConfiguration> => {
    const {
      instanceName: patchName,
      instanceStatus: patchStatus,
      ...dataFields
    } = toPatch(instanceFields);
    return {
      ...(dataFields as Partial<LargeUpsConfiguration>),
      id: configurationId,
      name: instanceName ?? (patchName as string | undefined),
      status: normalizeLargeUpsStatus(
        instanceStatus ?? (patchStatus as string | undefined),
      ),
    };
  }, [toPatch, instanceFields, configurationId, instanceName, instanceStatus]);

  const updateField = useCallback(
    <K extends keyof LargeUpsConfiguration>(
      field: K,
      value: LargeUpsConfiguration[K],
    ) => {
      if (field === 'name') {
        const name = typeof value === 'string' ? value : '';
        if (setInstanceName) {
          setInstanceName(name);
        } else {
          setInstanceField('instanceName', name);
        }
        return;
      }
      if (field === 'status') {
        const status = normalizeLargeUpsStatus(
          typeof value === 'string' ? value : undefined,
        );
        if (setInstanceStatus) {
          setInstanceStatus(status);
        } else {
          setInstanceField('instanceStatus', status);
        }
        return;
      }
      // The engine session stores editor forms: booleans stay booleans (yesNo),
      // a cleared value becomes blank — never 'undefined'.
      const editorValue =
        typeof value === 'boolean' ? value : value == null ? '' : String(value);
      setInstanceField(field as string, editorValue);
    },
    [setInstanceField, setInstanceName, setInstanceStatus],
  );

  const onFilesSelectedDirty = useMemo(
    () =>
      onFilesSelected
        ? (sectionId: string, files: Parameters<typeof onFilesSelected>[1]) => {
            markDirty();
            onFilesSelected(sectionId, files);
          }
        : undefined,
    [markDirty, onFilesSelected],
  );

  return (
    <LargeUpsEditFormContent
      formData={formData}
      updateField={updateField}
      disabled={disabled}
      requestStage={requestStage}
      requestId={requestId}
      attachmentsBySection={attachmentsBySection}
      onAttachFiles={onAttachFiles}
      onDeleteAttachment={onDeleteAttachment}
      selectedFiles={selectedFiles}
      onFilesSelected={onFilesSelectedDirty}
      isOffline={offlineMode}
      onCaptionChange={onCaptionChange}
      pendingCaptionEdits={pendingCaptionEdits}
      showAdditionalInformation={showAdditionalInformation}
      showFullDetails={showFullDetails}
    />
  );
}
